import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { DefaultFormDialogComponent } from '../../../../shared/message/dialogs/default-form-dialog.component';
import { GaValidateService } from '../../../../shared/formly/ga-validate.service';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { Override } from '../../../../shared/app-decorators';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { BPProfilesContactsDataService, IRole } from './bp-profiles-contacts-data.service';
import { BPProfilesOEMDataService } from '../oem/bp-profiles-oem-data.service';
import * as _ from 'underscore';
import { ICompany, IContactType, ICountryExtended } from '../../../../../type_definitions/datatypes';
import { util } from '../../../../shared/util';

import { CompanyType } from '../../../../shared/company/company-data.service';
import { RoleContactType } from '../../../../shared/contact/role-contact-type';

export enum ContactRoleDialogMode {
  EDIT = 'edit',
  NEW = 'new'
}

interface IRoleDialogModel extends IRole {
  contactSearchInCountry?:boolean;
}

const TRANSLATION_SCREEN:string = 'BPPROFILES.CONTACTS';

@Component({
  selector: 'contact-role-dialog',
  templateUrl: '../../../../shared/message/dialogs/default-form-dialog.html'
})
export class ContactRoleDialogComponent extends DefaultFormDialogComponent implements AfterViewInit, OnDestroy {
  @Input() public roleData:IRole[];
  @Input() public model:IRoleDialogModel;
  @Input() public profileId:number;
  @Input() public bpCompanyId:number;
  @Input() public oemCompanyId:number;
  @Input() public country:ICountryExtended;
  @Input() public mode:string;

  private contactField:IExtraFieldConfigurationObject;
  private contactTypeField:IExtraFieldConfigurationObject;
  private vendorField:IExtraFieldConfigurationObject;
  private contactCompanyField:IExtraFieldConfigurationObject;
  private contactSearchCheckboxField:IExtraFieldConfigurationObject;
  private mainContactField:IExtraFieldConfigurationObject;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private bpProfilesContactsDataService:BPProfilesContactsDataService,
    private bpProfilesOemDataService:BPProfilesOEMDataService,
    protected validateService:GaValidateService,
    protected translationService:TranslationService
  ) {
    super(validateService, translationService);
  }

  @Override(DefaultFormDialogComponent)
  public ngOnInit():void {
    super.ngOnInit();
    // add default model
    this.model = _.extend(this.model || {}, { contactSearchInCountry: true });
    this.fields = this.createFields();
    this.fillFields();
  }

  public ngAfterViewInit():void {
    this.onContactTypeChange(this.model.contactType);
    this.updateSearchCheckboxLabel();
    this.updateSearchContactUrl();
  }

  private fillFields():void {
    if (this.model?.contactCompany?.id) {
      this.formlyService.fillField(this.contactTypeField, this.bpProfilesContactsDataService.getRoleDefinitions(this.profileId, this.model?.contactCompany?.id));
    }

    this.formlyService.fillField(this.contactCompanyField, this.bpProfilesOemDataService.getPartnerCompanies(this.profileId))
      .then(() => {
        this.contactCompanyField.templateOptions.disabled = this.mode === ContactRoleDialogMode.EDIT && !!this.model?.id;
      });

    this.formlyService.fillField(this.vendorField, this.bpProfilesOemDataService.getPartnerCompanies(this.profileId, CompanyType.OEM));
  }

  private updateSearchContactUrl = (forBPCountry?:boolean):void => {
    this.contactField.templateOptions.url = this.bpProfilesContactsDataService.getContactSearchUrl(this.profileId, this.model.contactCompany?.id, !_.isUndefined(forBPCountry) ? forBPCountry : this.model.contactSearchInCountry);
  };

  private updateSearchCheckboxLabel = ():void => {
    this.contactSearchCheckboxField.templateOptions.label = this.translationService.instant('BPPROFILES.CONTACTS.CONTACTSEARCHINCOUNTRY.LABEL', { country: this.country?.name || ' ' });
  };

  private onContactTypeChange = (contactType:IContactType):void => {
    this.mainContactField.templateOptions.label = this.translationService.instant('BPPROFILES.CONTACTS.MAINCONTACT.LABEL', { role: this.model.contactType?.description || ' ' });
    // set required field state based on main contact status of this role
    // if no main contact status is set yet, then this will be required
    if (contactType) {
      const currentRoleMainContact:IRole = _.find(this.roleData, (role:IRole) => (role.contactType?.code === contactType.code) && role.mainContact);
      this.mainContactField.templateOptions.required = _.isUndefined(currentRoleMainContact) || currentRoleMainContact?.id === this.model?.id;
    }
  };

  private onContactCompanyChange = (company:ICompany, oldCompany:ICompany):void => {
    if ((company && oldCompany) && company.id !== oldCompany.id) {
      // when company selection is different from current value then clear values
      this.contactField.formControl.setValue(undefined);
      this.contactTypeField.formControl.setValue(undefined);
    }

    if (!_.isUndefined(company)) {
      this.updateSearchContactUrl();
      this.formlyService.fillField(this.contactTypeField, this.bpProfilesContactsDataService.getRoleDefinitions(this.profileId, company?.id));
      this.contactField.templateOptions.disabled = false;
    } else {
      // disabled contact field when company is cleared
      this.contactField.templateOptions.disabled = true;
    }
  };

  private get isMainContactAllowed():boolean {
    const allowedMainContactRoles:string[] = [
      RoleContactType.PSS,
      RoleContactType.COUNTRY_MANAGER,
      RoleContactType.NATIONAL_ACCOUNT_MANAGER
    ];

    return _.contains(allowedMainContactRoles, this.model?.contactType?.code);
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const fullWidthClass:string = 'col-sm-12';

    return [
      this.contactCompanyField = this.formlyService.aSelect('contactCompany')
        .isRequired()
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withLabelClass(fullWidthClass)
        .withValueClass(fullWidthClass)
        .withFormat(this.formattingService.formatCompany)
        .onChange(this.onContactCompanyChange)
        .build(),
      this.contactField = this.formlyService.aSearch('contact')
        .withUrl(this.bpProfilesContactsDataService.getContactSearchUrl(this.profileId, this.model.contactCompany?.id, this.model.contactSearchInCountry))
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withLabelClass(fullWidthClass)
        .withValueClass(fullWidthClass)
        .withFormat(this.formattingService.formatContact)
        .withGroupClass('no-margin-bottom')
        .withDisabled(_.isUndefined(this.model.contactCompany))
        .isRequired()
        .build(),
      this.contactSearchCheckboxField = this.formlyService.aCheckbox('contactSearchInCountry')
        .withoutWrapper()
        .withTranslationScreen(TRANSLATION_SCREEN)
        .onChange(this.updateSearchContactUrl)
        .withWrapperClass('mt-2 margin-bottom-sm')
        .build(),
      this.contactTypeField = this.formlyService.aSelect2('contactType')
        .isRequired()
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withLabelClass(fullWidthClass)
        .withValueClass(fullWidthClass)
        .withFormat(this.formattingService.formatContactType)
        .withExtraOptions({
          templateOptions: {
            allowClear: true,
            trackBy: 'code'
          }
        })
        .onChange(this.onContactTypeChange)
        .build(),
      this.mainContactField = this.formlyService.aCheckbox('mainContact')
        .isNotRequired()
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withValidationMessage('required', this.translationService.instant('BPPROFILES.CONTACTS.VALIDATION.MAIN_CONTACT'))
        .withoutWrapper()
        .withHideExpression(() => !this.isMainContactAllowed || _.isUndefined(this.model.contactType))
        .build(),
      this.formlyService.aTitle('BPPROFILES.CONTACTS.SCOPE_ROLE')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .build(),
      this.formlyService.aSelect2('country')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withFormat(this.formattingService.formatCountry)
        .withLabelClass(fullWidthClass)
        .withValueClass(fullWidthClass)
        .withExtraOptions({
          templateOptions: {
            allowClear: true
          }
        })
        .withOptions([ this.country ])
        .onChange(this.updateSearchCheckboxLabel)
        .build(),
      this.vendorField = this.formlyService.aSelect2('vendor')
        .withTranslationScreen(TRANSLATION_SCREEN)
        .withFormat(this.formattingService.formatCompany)
        .withLabelClass(fullWidthClass)
        .withValueClass(fullWidthClass)
        .withExtraOptions({
          templateOptions: {
            allowClear: true
          }
        })
        .build()
    ];
  }

  public save = ():void => {
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        const preparedModel:IRole = _.omit(_.clone(this.model), ['contactCompany', 'contactSearchInCountry']);
        const addEditContactRolePromise:Promise<IRole> = this.mode === ContactRoleDialogMode.EDIT?
          this.bpProfilesContactsDataService.editContactRole(preparedModel, this.profileId, this.model.id, this.bpCompanyId, this.oemCompanyId)
          :
          this.bpProfilesContactsDataService.addContactRole(preparedModel, this.profileId, this.bpCompanyId, this.oemCompanyId);

        this.saveButtonBusy = true;
        addEditContactRolePromise
          .then((data:IRole) => {
            this.onSave.emit(data);
          })
          .finally(() => {
            this.saveButtonBusy = false;
          });
      })
      .catch(util.noop);
  };

  public ngOnDestroy():void {
    this.bpProfilesOemDataService.clear();
  }
}